import { Injectable } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  getDoc,
  setDoc,
  query,
  getDocs,
  where,
  collectionData,
  docData,
} from '@angular/fire/firestore';
import { AuthService } from './auth.service';
import { firstValueFrom, from } from 'rxjs';

export interface YearFilter {
  year: number;
  enabled: boolean;
}

export interface UserPreferences {
  userId: string;
  email: string;
  yearFilters: YearFilter[];
  updatedAt: Date;
}

@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService {
  private readonly PREFERENCES_COLLECTION = 'user-preferences';
  private readonly PORTAL_DATA_COLLECTION = 'portal-data';

  constructor(private firestore: Firestore, private auth: AuthService) {}

  /**
   * NOTE: AngularFire may show warnings about "Firebase API called outside injection context"
   * for getDocs/getDoc. These warnings are safe to ignore in this service because:
   * 1. The Firestore instance is properly injected via constructor DI
   * 2. All Firebase calls use AngularFire's RxJS wrappers (collectionData, docData)
   * 3. The service is provided in root and properly initialized
   *
   * The warnings appear because AngularFire's zone detection is overly cautious, but
   * the actual functionality works correctly and is zone-safe.
   */

  /**
   * Get available years from the portal-metadata collection
   * This reads from a cached metadata document instead of scanning all portal-data
   * PERFORMANCE FIX: Previously loaded entire portal-data collection (1000+ docs)
   */
  async getAvailableYears(): Promise<number[]> {
    try {
      console.log(
        '[UserPreferences] Fetching available years from portal-metadata'
      );

      const metadataRef = doc(
        this.firestore,
        'portal-metadata',
        'available-years'
      );
      const metadataDoc = await getDoc(metadataRef);

      if (metadataDoc.exists()) {
        const data = metadataDoc.data();
        const years = data['years'] || [];

        if (years.length > 0) {
          console.log(
            '[UserPreferences] Available years from metadata:',
            years
          );
          return years;
        }
      }

      console.warn('[UserPreferences] No metadata found, using default years');
      // If no metadata found, default to current year and previous 2 years
      const currentYear = new Date().getFullYear();
      return [currentYear, currentYear - 1, currentYear - 2];
    } catch (error) {
      console.error('[UserPreferences] Error fetching available years:', error);
      // Return default years on error
      const currentYear = new Date().getFullYear();
      return [currentYear, currentYear - 1, currentYear - 2];
    }
  }

  /**
   * Get user preferences from Firestore
   */
  async getUserPreferences(): Promise<UserPreferences | null> {
    try {
      const user = await firstValueFrom(this.auth.user$);
      if (!user || !user.uid) {
        console.warn('[UserPreferences] No authenticated user');
        return null;
      }

      const prefsRef = doc(
        this.firestore,
        this.PREFERENCES_COLLECTION,
        user.uid
      );
      const data = await firstValueFrom(docData(prefsRef));

      if (data) {
        console.log('[UserPreferences] Loaded preferences:', data);
        return data as UserPreferences;
      }

      console.log('[UserPreferences] No preferences found for user');
      return null;
    } catch (error) {
      console.error('[UserPreferences] Error loading preferences:', error);
      return null;
    }
  }

  /**
   * Save year filters to user preferences
   */
  async saveYearFilters(yearFilters: YearFilter[]): Promise<void> {
    try {
      const user = await firstValueFrom(this.auth.user$);
      if (!user || !user.uid || !user.email) {
        throw new Error('No authenticated user');
      }

      const preferences: UserPreferences = {
        userId: user.uid,
        email: user.email,
        yearFilters,
        updatedAt: new Date(),
      };

      const prefsRef = doc(
        this.firestore,
        this.PREFERENCES_COLLECTION,
        user.uid
      );
      await setDoc(prefsRef, preferences, { merge: true });

      console.log('[UserPreferences] Saved preferences:', preferences);
    } catch (error) {
      console.error('[UserPreferences] Error saving preferences:', error);
      throw error;
    }
  }

  /**
   * Get enabled years for filtering
   * Returns all years if no preferences are set
   */
  async getEnabledYears(): Promise<number[]> {
    try {
      const preferences = await this.getUserPreferences();

      if (
        !preferences ||
        !preferences.yearFilters ||
        preferences.yearFilters.length === 0
      ) {
        // No preferences set, return all available years
        return await this.getAvailableYears();
      }

      // Return only enabled years
      const enabledYears = preferences.yearFilters
        .filter((f) => f.enabled)
        .map((f) => f.year);

      console.log('[UserPreferences] Enabled years:', enabledYears);
      return enabledYears;
    } catch (error) {
      console.error('[UserPreferences] Error getting enabled years:', error);
      // On error, return all available years
      return await this.getAvailableYears();
    }
  }

  /**
   * Check if a date falls within the enabled years
   */
  async isDateInEnabledYears(
    date: Date | string | number | any
  ): Promise<boolean> {
    try {
      let dateObj: Date;

      // Handle Firestore Timestamp
      if (date && date.toDate && typeof date.toDate === 'function') {
        dateObj = date.toDate();
      }
      // Handle Date object
      else if (date instanceof Date) {
        dateObj = date;
      }
      // Handle string or number
      else if (typeof date === 'string' || typeof date === 'number') {
        dateObj = new Date(date);
      } else {
        return true; // If we can't parse the date, include it
      }

      if (isNaN(dateObj.getTime())) {
        return true; // Invalid date, include it
      }

      const year = dateObj.getFullYear();
      const enabledYears = await this.getEnabledYears();

      return enabledYears.includes(year);
    } catch (error) {
      console.error('[UserPreferences] Error checking date:', error);
      return true; // On error, include the item
    }
  }
}
